/**
 * WebRTC 连接管理
 * 负责 P2P 连接建立、ICE 协商、DataChannel 管理
 */
export class WebRTCConnection {
  constructor(options = {}) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.controlChannel = null;
    this.isInitiator = options.isInitiator || false;
    this.transferId = options.transferId || '';
    this.signaling = options.signaling || null;
    this._pendingCandidates = [];
    this._closed = false;

    this.iceServers = options.iceServers || [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ];

    this.useTrickleIce = options.useTrickleIce !== false;

    // 回调
    this.onDataChannelOpen = options.onDataChannelOpen || (() => {});
    this.onControlMessage = options.onControlMessage || (() => {});
    this.onDataMessage = options.onDataMessage || (() => {});
    this.onConnectionStateChange = options.onConnectionStateChange || (() => {});
  }

  /**
   * 初始化 PeerConnection
   */
  async init() {
    const config = {
      iceServers: this.iceServers,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceCandidatePoolSize: 10
    };

    this.peerConnection = new RTCPeerConnection(config);

    // ICE 候选事件
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.useTrickleIce && this.signaling) {
        this.signaling.sendCandidate(this.transferId, event.candidate.toJSON());
      }
    };

    // 连接状态变化
    this.peerConnection.onconnectionstatechange = () => {
      if (!this.peerConnection) return;
      const state = this.peerConnection.connectionState;
      this.onConnectionStateChange(state);
    };

    // 接收方：监听 DataChannel
    this.peerConnection.ondatachannel = (event) => {
      const channel = event.channel;
      if (channel.label === 'control') {
        this.controlChannel = channel;
        this._setupControlChannel(channel);
      } else if (channel.label === 'fileData') {
        this.dataChannel = channel;
        this._setupDataChannel(channel);
      }
    };

    // 发送方：创建 DataChannel
    if (this.isInitiator) {
      this.controlChannel = this.peerConnection.createDataChannel('control', { ordered: true });
      this._setupControlChannel(this.controlChannel);

      this.dataChannel = this.peerConnection.createDataChannel('fileData', { ordered: false });
      this._setupDataChannel(this.dataChannel);
    }
  }

  /**
   * 创建 Offer（发送方调用）
   */
  async createOffer() {
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    // 等待 ICE 收集完成，最多 5 秒
    if (this.peerConnection.iceGatheringState === 'complete') {
      return this.peerConnection.localDescription;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(this.peerConnection.localDescription);
      }, 5000);

      const check = () => {
        if (this.peerConnection && this.peerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve(this.peerConnection.localDescription);
        }
      };

      this.peerConnection.onicegatheringstatechange = check;
      check();
    });
  }

  /**
   * 创建 Answer（接收方调用）
   */
  async createAnswer(offerSdp) {
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: offerSdp }));
    await this._flushPendingCandidates();

    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);

    if (this.peerConnection.iceGatheringState === 'complete') {
      return this.peerConnection.localDescription;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(this.peerConnection.localDescription);
      }, 10000);

      const check = () => {
        if (this.peerConnection && this.peerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve(this.peerConnection.localDescription);
        }
      };

      this.peerConnection.onicegatheringstatechange = check;
      check();
    });
  }

  /**
   * 设置远端 Answer
   */
  async setRemoteAnswer(answerSdp) {
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSdp }));
    await this._flushPendingCandidates();
  }

  /**
   * 添加 ICE 候选
   */
  async addIceCandidate(candidate) {
    if (!this.peerConnection || !candidate) return;
    if (this.peerConnection.remoteDescription) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn('[IceShuttle WebRTC] 添加 ICE 候选失败:', e.message);
      }
    } else {
      this._pendingCandidates.push(candidate);
    }
  }

  /**
   * 通过控制通道发送消息
   */
  sendControl(message) {
    if (this.controlChannel && this.controlChannel.readyState === 'open') {
      this.controlChannel.send(JSON.stringify(message));
    }
  }

  /**
   * 通过数据通道发送 ArrayBuffer
   */
  async sendData(buffer) {
    if (this._closed) return false;
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return false;

    const maxMsgSize = this.maxMessageSize;
    if (buffer.byteLength > maxMsgSize) return false;

    // 流控
    const HIGH_THRESHOLD = 4 * 1024 * 1024;
    const LOW_THRESHOLD = 1 * 1024 * 1024;

    if (this.dataChannel.bufferedAmount > HIGH_THRESHOLD) {
      await new Promise((resolve) => {
        if (this._closed || !this.dataChannel) { resolve(); return; }
        this.dataChannel.bufferedAmountLowThreshold = LOW_THRESHOLD;
        const onLow = () => {
          if (this.dataChannel) this.dataChannel.removeEventListener('bufferedamountlow', onLow);
          resolve();
        };
        this.dataChannel.addEventListener('bufferedamountlow', onLow);
        setTimeout(resolve, 5000);
      });
    }

    if (this._closed || !this.dataChannel || this.dataChannel.readyState !== 'open') return false;

    try {
      this.dataChannel.send(buffer);
      return true;
    } catch (e) {
      console.error('[IceShuttle WebRTC] 发送数据失败:', e.message);
      return false;
    }
  }

  get isDataChannelReady() {
    return this.dataChannel && this.dataChannel.readyState === 'open';
  }

  get maxMessageSize() {
    if (this.peerConnection && this.peerConnection.sctp) {
      return this.peerConnection.sctp.maxMessageSize;
    }
    return 262144;
  }

  /**
   * 关闭连接
   */
  close() {
    this._closed = true;
    if (this.dataChannel) { try { this.dataChannel.close(); } catch (e) {} this.dataChannel = null; }
    if (this.controlChannel) { try { this.controlChannel.close(); } catch (e) {} this.controlChannel = null; }
    if (this.peerConnection) { try { this.peerConnection.close(); } catch (e) {} this.peerConnection = null; }
  }

  // ---- 内部方法 ----

  _setupControlChannel(channel) {
    channel.onopen = () => console.log('[IceShuttle WebRTC] 控制通道已打开');
    channel.onmessage = (event) => {
      try {
        this.onControlMessage(JSON.parse(event.data));
      } catch (e) {}
    };
  }

  _setupDataChannel(channel) {
    channel.onopen = () => {
      console.log('[IceShuttle WebRTC] 数据通道已打开');
      this.onDataChannelOpen();
    };
    channel.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.onDataMessage(event.data);
      }
    };
    channel.onclose = () => console.log('[IceShuttle WebRTC] 数据通道已关闭');
  }

  async _flushPendingCandidates() {
    const candidates = this._pendingCandidates.splice(0);
    for (const candidate of candidates) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn('[IceShuttle WebRTC] 添加缓存 ICE 候选失败:', e.message);
      }
    }
  }
}
